// PR 5: preview-only card for an XLSX conversation attachment. Rendered from
// the `chat_file_preview_v1` payload (workbook parsing + auto-detected
// mapping, server-side; no import is executed from this card). The payload
// type mirrors the backend service contract structurally — the frontend never
// imports API services.
import { FileSpreadsheet } from 'lucide-react';
import type { ReactElement } from 'react';

import { useI18nSubscription } from '../../../i18n/useI18nSubscription.js';
import { i18n } from '../../../../i18n/index.js';

/** Structural mirror of the backend `ChatFilePreviewV1Payload`. */
export interface ChatFilePreviewV1 {
  readonly type: 'chat_file_preview_v1';
  readonly attachmentId: string;
  readonly fileName: string;
  readonly format: string;
  readonly rows: number;
  readonly headers: readonly string[];
  readonly mapping: Readonly<Record<string, string>>;
  readonly sampleRows: readonly (readonly string[])[];
}

const VISIBLE_HEADER_LIMIT = 6;
const VISIBLE_SAMPLE_ROW_LIMIT = 5;
const VISIBLE_SAMPLE_CELL_LIMIT = 6;
const VISIBLE_MAPPING_LIMIT = 8;

interface ChatFilePreviewSnippetProps {
  preview: ChatFilePreviewV1;
}

export function ChatFilePreviewSnippet({ preview }: ChatFilePreviewSnippetProps): ReactElement {
  useI18nSubscription();
  const headers = preview.headers.slice(0, VISIBLE_HEADER_LIMIT);
  const headerOverflow = preview.headers.length - headers.length;
  const mappingEntries = Object.entries(preview.mapping).slice(0, VISIBLE_MAPPING_LIMIT);
  const sampleRows = preview.sampleRows
    .slice(0, VISIBLE_SAMPLE_ROW_LIMIT)
    .map((row) => row.slice(0, VISIBLE_SAMPLE_CELL_LIMIT));
  const sampleColumns = Math.max(
    ...sampleRows.map((row) => row.length),
    headers.length > 0 ? headers.length : 1,
  );

  return (
    <section
      data-testid="chat-file-preview-card"
      className="rounded-lg border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900"
    >
      <header className="flex items-start gap-2 border-b border-stone-100 dark:border-stone-800 px-4 py-3">
        <FileSpreadsheet
          size={15}
          aria-hidden="true"
          className="mt-0.5 shrink-0 text-emerald-600"
        />
        <div className="min-w-0">
          <h3 className="m-0 text-sm font-semibold text-gray-900 dark:text-stone-100">
            {i18n.t('crm.ai_chat.file_input.preview.title')}
          </h3>
          <p className="mt-0.5 truncate text-xs text-gray-500 dark:text-stone-400">
            {preview.fileName} · {preview.format.toUpperCase()} ·{' '}
            {i18n.tc('crm.ai_chat.file_input.preview.rows', preview.rows)}
          </p>
        </div>
      </header>

      <div className="space-y-3 px-4 py-3 text-xs">
        <div>
          <p className="m-0 mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-stone-500">
            {i18n.t('crm.ai_chat.file_input.preview.columns')}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {headers.map((header) => (
              <span
                key={header}
                className="inline-flex items-center rounded-full border border-stone-200 dark:border-stone-700 bg-stone-50 dark:bg-stone-800 px-2 py-0.5 font-medium text-stone-700 dark:text-stone-200"
              >
                {header}
              </span>
            ))}
            {headerOverflow > 0 && (
              <span className="inline-flex items-center px-1 py-0.5 font-medium text-gray-400 dark:text-stone-500">
                +{headerOverflow}
              </span>
            )}
          </div>
        </div>

        <div>
          <p className="m-0 mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-stone-500">
            {i18n.t('crm.ai_chat.file_input.preview.mapping')}
          </p>
          {mappingEntries.length === 0 ? (
            <p className="m-0 text-gray-500 dark:text-stone-400">
              {i18n.t('crm.ai_chat.file_input.preview.mapping_empty')}
            </p>
          ) : (
            <ul className="m-0 list-none space-y-1 p-0">
              {mappingEntries.map(([column, field]) => (
                <li key={column} className="flex flex-wrap items-center gap-1">
                  <span className="font-medium text-stone-700 dark:text-stone-200">{column}</span>
                  <span aria-hidden="true" className="text-gray-400 dark:text-stone-500">
                    →
                  </span>
                  <span className="rounded bg-emerald-50 dark:bg-emerald-950/40 px-1.5 py-0.5 font-medium text-emerald-700 dark:text-emerald-300">
                    {field}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        {sampleRows.length > 0 && (
          <div>
            <p className="m-0 mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400 dark:text-stone-500">
              {i18n.t('crm.ai_chat.file_input.preview.sample')}
            </p>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-stone-100 dark:border-stone-800 text-[11px] font-medium text-gray-500 dark:text-stone-400">
                    {Array.from({ length: sampleColumns }, (_, index) => (
                      <th key={index} className="px-2 py-1.5">
                        {headers[index] ?? ''}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sampleRows.map((row, rowIndex) => (
                    <tr
                      key={rowIndex}
                      className="border-b border-stone-50 dark:border-stone-800/60 last:border-0"
                    >
                      {Array.from({ length: sampleColumns }, (_, cellIndex) => (
                        <td
                          key={cellIndex}
                          className="max-w-40 truncate px-2 py-1.5 text-gray-700 dark:text-stone-300"
                        >
                          {row[cellIndex] ?? ''}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
